import meanBy from 'lodash.meanby';
import React, { useEffect, useMemo, useState } from 'react';
import scssVariables from '../../../../../Config.module.scss';
import { Currency, DemeterDataFrequency, MarketIndicatorOutlook, RunMarketIndicatorResponse, UnitOfMeasure } from '../../../../../Generated/Raven-Demeter';
import {
    defaultSeasonalEndMonth,
    defaultSeasonalStartMonth,
} from '../../../../Pages/Administration/MarketIndicatorsManagement/MarketIndicatorFactor/MarketIndicatorFactorPageDefault';
import {
    AverageSeasonalParameters,
    leadingIndicatorTypeDefinitions,
} from '../../../../Pages/Administration/MarketIndicatorsManagement/MarketIndicatorsManagementDefinitions';
import formattingService from '../../../../Services/Formatting/FormattingService';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import { chartColors, IChartDataSeries } from '../../ChartDefinitions';
import chartService from '../../ChartService';
import ChartWrapper from '../../ChartWrapper/ChartWrapper';
import { PlotBand } from '../../MarketIndicator/MarketIndicatorChartRaw';
import marketIndicatorChartDateService from '../MarketIndicatorChartDateService';
import { defaultAverageSeasonalParameters } from '../MarketIndicatorChartDefaultParameters';
import marketIndicatorChartService from '../MarketIndicatorChartService';
import { defaultChartPalette, IMarketIndicatorUserChartBaseProps, ISeasonalSeries } from '../MarketIndicatorUserChartDefinitions';
import MarketIndicatorUserChartRaw from '../MarketIndicatorUserChartRaw';

export interface IMarketIndicatorAverageSeasonalChartProps extends IMarketIndicatorUserChartBaseProps {
    runMarketIndicatorResponse?: RunMarketIndicatorResponse;
    templateTypeIsAverageCount?: boolean;
    parameters?: AverageSeasonalParameters;
}

const separarteAxisThresholdPercent = 0.1;
const plotBandOffesetDays = 7;

const MarketIndicatorAverageSeasonalChart: React.FC<IMarketIndicatorAverageSeasonalChartProps> = (props: IMarketIndicatorAverageSeasonalChartProps) => {
    const [translations, translate] = useLanguage();
    const [lineSeries, setLineSeries] = useState<IChartDataSeries[]>([]);

    const numberOfYears = useMemo(() => {
        const parameters = props.parameters ?? defaultAverageSeasonalParameters;
        const value = Number(parameters.yearsOfData);
        return value;
    }, [props.parameters]);

    const chartLinesSeriesData = useMemo<ISeasonalSeries | null>(() => {
        if (
            !props.runTestMarketIndicatorFactorResponse ||
            !props.runTestMarketIndicatorFactorResponse?.rows ||
            !props.runTestMarketIndicatorFactorResponse.rows.length ||
            !props.parameters
        ) {
            return null;
        }

        const dataForSeries = marketIndicatorChartService.getAverageSeasonalChartSeriesData(
            props.runTestMarketIndicatorFactorResponse.rows,
            numberOfYears,
            props.parameters?.startMonth,
            props.parameters?.endMonth,
        );

        return dataForSeries;
    }, [props.runTestMarketIndicatorFactorResponse, props.parameters, numberOfYears]);

    const isLoading = useMemo(
        () => props.isLoading || !props.runTestMarketIndicatorFactorResponse,
        [props.runTestMarketIndicatorFactorResponse, props.isLoading],
    );

    const title = useMemo(() => {
        if (props.title) {
            return props.title;
        }

        if (props.runTestMarketIndicatorFactorResponse && props.runTestMarketIndicatorFactorResponse.commodityDisplayName) {
            const region = props.runTestMarketIndicatorFactorResponse?.subRegion
                ? translate(props.runTestMarketIndicatorFactorResponse?.subRegion)
                : translate(props.runTestMarketIndicatorFactorResponse?.regionDisplayName!);
            const tableDefinitionType =
                translations.tableDefinitionType[
                    leadingIndicatorTypeDefinitions.find((x) => x.value === props.runTestMarketIndicatorFactorResponse?.leadingIndicatorType)
                        ?.tableDefinitionType!
                ];
            const commdityName = props.runTestMarketIndicatorFactorResponse.commodityDisplayName;
            return [region, commdityName, tableDefinitionType].filter((x) => x !== null && x !== '').join(' ');
        }

        return '';
    }, [props.runTestMarketIndicatorFactorResponse]);

    const sourceTag = useMemo(() => {
        if (
            props.runTestMarketIndicatorFactorResponse &&
            props.runTestMarketIndicatorFactorResponse.dataSourceTags &&
            props.runTestMarketIndicatorFactorResponse.dataSourceTags.length > 0
        ) {
            return `${props.runTestMarketIndicatorFactorResponse.dataSourceTags[0]}, ${translations.dataSource.StoneXCalculations}`;
        }

        return '';
    }, [props.runTestMarketIndicatorFactorResponse]);

    const currencies = useMemo(() => {
        if (props.runTestMarketIndicatorFactorResponse && props.runTestMarketIndicatorFactorResponse.currency) {
            return lineSeries[1]?.yAxis === 1
                ? [props.runTestMarketIndicatorFactorResponse.currency, props.runTestMarketIndicatorFactorResponse.currency]
                : [props.runTestMarketIndicatorFactorResponse.currency];
        }

        return ['' as Currency];
    }, [props.runTestMarketIndicatorFactorResponse, lineSeries]);

    const unitOfMeasures = useMemo(() => {
        if (props.runTestMarketIndicatorFactorResponse && props.runTestMarketIndicatorFactorResponse.unitOfMeasure) {
            return lineSeries[1]?.yAxis === 1
                ? [props.runTestMarketIndicatorFactorResponse.unitOfMeasure, props.runTestMarketIndicatorFactorResponse.unitOfMeasure]
                : [props.runTestMarketIndicatorFactorResponse.unitOfMeasure];
        }

        return ['' as UnitOfMeasure];
    }, [props.runTestMarketIndicatorFactorResponse, lineSeries]);

    const displayUnitOfMeasure = useMemo(() => {
        if (props.runTestMarketIndicatorFactorResponse && props.runTestMarketIndicatorFactorResponse.dataCombinationDisplayUnits) {
            return props.runTestMarketIndicatorFactorResponse.dataCombinationDisplayUnits;
        }

        return '' as UnitOfMeasure;
    }, [props.runTestMarketIndicatorFactorResponse]);

    useEffect(() => {
        if (!chartLinesSeriesData || !chartLinesSeriesData.thisYearLineSeries.length || !chartLinesSeriesData.nYearAverageLineSeries.length) {
            return;
        }

        const startMonth = props.parameters?.startMonth ?? defaultSeasonalStartMonth;
        const endMonth = props.parameters?.endMonth ?? defaultSeasonalEndMonth;
        const minimumChartDisplayDate = new Date(new Date().setMonth(+startMonth - 1));
        const maximumChartDisplayDate = new Date(new Date().setMonth(+endMonth - 1));
        const isMonthly = props.runTestMarketIndicatorFactorResponse?.dataFrequency === DemeterDataFrequency.Monthly;
        minimumChartDisplayDate.setDate(1);
        const lastMonthOfCalculationData =
            (chartLinesSeriesData.thisYearLineSeries[chartLinesSeriesData.thisYearLineSeries.length - 1]?.asOfDate.getMonth() ?? -1) + 1;
        const startMonthIsAfterCurrentData = lastMonthOfCalculationData < +startMonth;
        const lastYearOnly = +startMonth < +endMonth && startMonthIsAfterCurrentData;
        if (lastYearOnly) {
            minimumChartDisplayDate.setFullYear(minimumChartDisplayDate.getFullYear() - 1);
            maximumChartDisplayDate.setFullYear(maximumChartDisplayDate.getFullYear() - 1);
        }

        const thisYearLineSeries = !isMonthly
            ? marketIndicatorChartDateService.toWeeklySeriesData(chartLinesSeriesData.thisYearLineSeries, minimumChartDisplayDate, true, true)
            : chartLinesSeriesData.thisYearLineSeries;
        const nYearAverageLineSeries = !isMonthly
            ? marketIndicatorChartDateService.toWeeklySeriesData(chartLinesSeriesData.nYearAverageLineSeries, minimumChartDisplayDate)
            : chartLinesSeriesData.nYearAverageLineSeries;

        const newLineSeries: IChartDataSeries[] = [
            {
                label: translations.text.thisYear,
                data: thisYearLineSeries,
                axisLabel: props.yAxisLabel,
                color: props.runMarketIndicatorResponse?.rows ? scssVariables.black : undefined,
            },
            {
                label: `${numberOfYears} ${translations.text.yearlyAverage}`,
                data: nYearAverageLineSeries,
                axisLabel: props.yAxisLabel,
                color: props.runMarketIndicatorResponse?.rows ? chartColors.lineChartColorsRuleSetOne[3] : undefined,
            },
        ];

        // Check if the lines need to be on their own Y-axis.
        const average1 = meanBy(newLineSeries[0].data, 'value');
        const average2 = meanBy(newLineSeries[1].data, 'value');
        if (Math.abs((average1 - average2) / Math.min(Math.abs(average1), Math.abs(average2))) > separarteAxisThresholdPercent) {
            newLineSeries[0].yAxis = 0;
            newLineSeries[1].yAxis = props.templateTypeIsAverageCount ? 0 : 1;
        }

        setLineSeries(newLineSeries);
    }, [chartLinesSeriesData]);

    const minimumChartDate = lineSeries && lineSeries[1] ? lineSeries[1].data[0]?.asOfDate : new Date();
    const maximumChartDate = lineSeries && lineSeries[1] ? lineSeries[1].data[lineSeries[1].data.length - 1]?.asOfDate : new Date();

    const plotBands = useMemo(() => {
        if (!props.runMarketIndicatorResponse?.rows) {
            return [];
        }

        if (!lineSeries || !lineSeries[1] || lineSeries[1].data.length === 0) {
            return [];
        }

        const nYearAverageLineData = lineSeries[1].data;
        const newBands: PlotBand[] = [];
        const marketDataByDates = props.runMarketIndicatorResponse?.rows;
        const filteredDataByDates = marketDataByDates?.filter((x) => x.outlook !== MarketIndicatorOutlook.Unavailable);

        if (!filteredDataByDates || filteredDataByDates.length === 0) {
            return [];
        }

        const outlooksByWeek = new Map<number, MarketIndicatorOutlook>();

        filteredDataByDates.forEach((outlookData) => {
            const outlookDate = new Date(outlookData.asOfDate);
            const weekNumber = formattingService.toWeekNumber(outlookDate);

            outlooksByWeek.set(weekNumber, outlookData.outlook);
        });

        let currentBand: PlotBand | null = null;

        nYearAverageLineData.forEach((dataPoint, index) => {
            const dataPointDate = new Date(dataPoint.asOfDate);
            const currentYearEquivalentDate = new Date(dataPointDate);
            currentYearEquivalentDate.setFullYear(new Date().getFullYear());

            const weekNumber = formattingService.toWeekNumber(currentYearEquivalentDate);
            const outlook = outlooksByWeek.get(weekNumber);

            if (!outlook) {
                if (currentBand) {
                    const adjustedToDate = new Date(dataPoint.asOfDate);
                    adjustedToDate.setDate(adjustedToDate.getDate() - plotBandOffesetDays);
                    currentBand.toDate = adjustedToDate.getTime();
                    newBands.push(currentBand);
                    currentBand = null;
                }

                return;
            }

            const outlookColor = chartService.getOutlookColor(outlook, true, true);
            const adjustedFromDate = new Date(dataPoint.asOfDate);
            adjustedFromDate.setDate(adjustedFromDate.getDate() - plotBandOffesetDays);

            if (!currentBand) {
                currentBand = {
                    fromDate: adjustedFromDate.getTime(),
                    toDate: adjustedFromDate.getTime(),
                    color: outlookColor,
                };
            } else if (currentBand.color === outlookColor) {
                currentBand.toDate = adjustedFromDate.getTime();
            } else {
                currentBand.toDate = adjustedFromDate.getTime();
                newBands.push(currentBand);
                currentBand = {
                    fromDate: adjustedFromDate.getTime(),
                    toDate: adjustedFromDate.getTime(),
                    color: outlookColor,
                };
            }

            if (index === nYearAverageLineData.length - 1 && currentBand) {
                const adjustedToDate = new Date(dataPoint.asOfDate);
                adjustedToDate.setDate(adjustedToDate.getDate());
                currentBand.toDate = adjustedToDate.getTime();
                newBands.push(currentBand);
            }
        });

        return newBands;
    }, [props.runMarketIndicatorResponse, lineSeries, chartLinesSeriesData]);

    return (
        <ChartWrapper
            name="MarketIndicatorAverageSeasonalChart"
            title={title}
            customTitle={props.customTitle}
            marketIndicatorOutlook={props.marketIndicatorOutlook}
            dataSourceTag={sourceTag}
            isLoading={isLoading}
            headerOptions={{
                showOnlyAsPopout: props.showOnlyAsPopout,
            }}
            showPopout={props.showPopout}
            setShowPopout={props.setShowPopout}
            testId="MarketIndicatorFactorChart"
            track={{ eventCategory: 'MarketIndicators', eventAction: 'MarketIndicatorAverageSeasonalChart' }}
        >
            <MarketIndicatorUserChartRaw
                lineSeries={lineSeries}
                currencies={currencies}
                plotBands={plotBands}
                unitOfMeasures={unitOfMeasures}
                displayUnitOfMeasure={displayUnitOfMeasure}
                displayWeekNumberTooltips={props.templateTypeIsAverageCount}
                colorPalette={defaultChartPalette}
                startOnTick
                minimumAxisDate={minimumChartDate?.getTime()}
                maximumAxisDate={maximumChartDate?.getTime()}
                disableTooltip={chartService.isRestrictedDataSource(...(props.runTestMarketIndicatorFactorResponse?.dataSourceTags ?? []))}
            />
        </ChartWrapper>
    );
};

export default MarketIndicatorAverageSeasonalChart;
